/**
 * Media Gallery Slider Component
 * Optimized slider with smooth drag handling and reliable slide detection
 * Only applies new drag logic on mobile devices
 */
class MediaGallerySlider extends HTMLElement {
  constructor() {
    super();
    this.slider = this.querySelector('[id^="Slider-"]');
    this.sliderItems = this.querySelectorAll('[id^="Slide-"]');
    this.prevButton = this.querySelector('button[name="previous"]');
    this.nextButton = this.querySelector('button[name="next"]');
    this.dotsContainer = this.querySelector('.media--dots');

    if (!this.slider || !this.sliderItems.length) return;

    // Check if mobile (750px breakpoint matches theme)
    this.isMobile = window.innerWidth <= 749;
    this.mql = window.matchMedia('(max-width: 749px)');
    
    // Listen for screen size changes
    this.mql.addEventListener('change', this.handleResize.bind(this));

    // State
    this.currentSlide = 0;
    this.isDragging = false;
    this.startX = 0;
    this.currentX = 0;
    this.scrollLeft = 0;
    this.velocity = 0;
    this.lastX = 0;
    this.lastTime = 0;
    this.animationFrame = null;

    // Initialize
    this.init();
  }

  handleResize(e) {
    this.isMobile = e.matches;
    // Reinitialize handlers based on new screen size
    if (this.isMobile) {
      this.setupDragHandlers();
    } else {
      this.removeDragHandlers();
    }
  }

  init() {
    // Set initial active slide silently — slide 0 is already is-active from server HTML
    this.setActiveSlide(0, false, false, true);

    // Initialize dots
    this.initDots();

    // Setup button handlers
    if (this.prevButton) {
      this.prevButton.addEventListener('click', () => this.goToSlide(this.currentSlide - 1));
    }
    if (this.nextButton) {
      this.nextButton.addEventListener('click', () => this.goToSlide(this.currentSlide + 1));
    }

    // Only setup drag/touch handlers on mobile
    if (this.isMobile) {
      this.setupDragHandlers();
    }

    // Setup scroll handler for programmatic scrolling
    this.slider.addEventListener('scroll', this.handleScroll.bind(this), { passive: true });

    // Handle resize
    const resizeObserver = new ResizeObserver(() => {
      this.updateButtonStates();
      if (this.isMobile) {
        this.snapToSlide(this.currentSlide);
      }
    });
    resizeObserver.observe(this.slider);

    // Initial button state
    this.updateButtonStates();
  }

  // Compatibility method for media-gallery.js
  resetPages() {
    // Refresh slider items in case DOM changed
    this.sliderItems = this.querySelectorAll('[id^="Slide-"]');
    this.initDots();
    this.updateButtonStates();
  }

  initDots() {
    if (!this.dotsContainer) return;

    // Clear existing dots
    this.dotsContainer.innerHTML = '';

    // Create dots
    this.sliderItems.forEach((_, index) => {
      const dot = document.createElement('li');
      dot.dataset.index = index;
      dot.addEventListener('click', () => this.goToSlide(index));
      this.dotsContainer.appendChild(dot);
    });

    this.updateDots();
  }

  setupDragHandlers() {
    // Only setup on mobile
    if (!this.isMobile) return;

    // Pointer events for unified touch/mouse handling
    this.dragStartHandler = this.onDragStart.bind(this);
    this.dragMoveHandler = this.onDragMove.bind(this);
    this.dragEndHandler = this.onDragEnd.bind(this);
    this.selectStartHandler = (e) => {
      if (this.isDragging) e.preventDefault();
    };

    this.slider.addEventListener('pointerdown', this.dragStartHandler);
    this.slider.addEventListener('pointermove', this.dragMoveHandler);
    this.slider.addEventListener('pointerup', this.dragEndHandler);
    this.slider.addEventListener('pointercancel', this.dragEndHandler);
    this.slider.addEventListener('selectstart', this.selectStartHandler);

    // CSS to improve drag experience
    this.slider.style.touchAction = 'pan-y pinch-zoom';
    this.slider.style.userSelect = 'none';
  }

  removeDragHandlers() {
    if (this.dragStartHandler) {
      this.slider.removeEventListener('pointerdown', this.dragStartHandler);
      this.slider.removeEventListener('pointermove', this.dragMoveHandler);
      this.slider.removeEventListener('pointerup', this.dragEndHandler);
      this.slider.removeEventListener('pointercancel', this.dragEndHandler);
      this.slider.removeEventListener('selectstart', this.selectStartHandler);
    }
    this.slider.style.touchAction = '';
    this.slider.style.userSelect = '';
  }

  onDragStart(e) {
    // Only handle on mobile
    if (!this.isMobile) return;
    // Only handle left mouse button or touch
    if (e.pointerType === 'mouse' && e.button !== 0) return;

    this.isDragging = true;
    this.startX = e.pageX - this.slider.offsetLeft;
    this.scrollLeft = this.slider.scrollLeft;
    this.lastX = e.pageX;
    this.lastTime = Date.now();
    this.velocity = 0;

    this.slider.style.cursor = 'grabbing';
    this.slider.style.scrollSnapType = 'none';

    // Cancel any ongoing animation
    if (this.animationFrame) {
      cancelAnimationFrame(this.animationFrame);
      this.animationFrame = null;
    }
  }

  onDragMove(e) {
    if (!this.isDragging) return;

    e.preventDefault();

    const currentTime = Date.now();
    const currentX = e.pageX - this.slider.offsetLeft;
    const walk = (currentX - this.startX) * 1.5; // Scroll speed multiplier
    this.slider.scrollLeft = this.scrollLeft - walk;

    // Calculate velocity for momentum scrolling
    const timeDelta = currentTime - this.lastTime;
    if (timeDelta > 0) {
      const xDelta = currentX - this.lastX;
      this.velocity = xDelta / timeDelta;
    }

    this.lastX = currentX;
    this.lastTime = currentTime;
  }

  onDragEnd(e) {
    if (!this.isDragging || !this.isMobile) return;

    this.isDragging = false;
    this.slider.style.cursor = '';
    this.slider.style.scrollSnapType = 'x mandatory';

    // Determine target slide based on scroll position and velocity
    const threshold = 50; // Minimum pixels to trigger slide change
    const velocityThreshold = 0.5; // Velocity threshold for momentum

    let targetSlide = this.currentSlide;

    // Use velocity for momentum-based sliding
    if (Math.abs(this.velocity) > velocityThreshold) {
      targetSlide = this.velocity > 0 
        ? Math.max(0, this.currentSlide - 1)
        : Math.min(this.sliderItems.length - 1, this.currentSlide + 1);
    } else {
      // Use scroll position to determine target slide
      const scrollPosition = this.slider.scrollLeft;
      const slideWidth = this.getSlideWidth();
      const slideIndex = Math.round(scrollPosition / slideWidth);
      targetSlide = Math.max(0, Math.min(this.sliderItems.length - 1, slideIndex));
    }

    // Snap to the determined slide
    this.goToSlide(targetSlide, true);
  }

  handleScroll() {
    // Only update if not dragging (to avoid conflicts)
    if (this.isDragging && this.isMobile) return;

    // Throttle all scroll events (mobile and desktop) via requestAnimationFrame
    if (this.animationFrame) return;

    this.animationFrame = requestAnimationFrame(() => {
      this.updateActiveSlideFromScroll();
      this.animationFrame = null;
    });
  }

  updateActiveSlideFromScroll() {
    if (!this.sliderItems.length) return;

    const scrollPosition = this.slider.scrollLeft;
    const containerCenter = scrollPosition + this.slider.clientWidth / 2;
    
    // Find which slide is most centered in the viewport
    let closestSlideIndex = 0;
    let closestDistance = Infinity;

    this.sliderItems.forEach((slide, index) => {
      const slideCenter = slide.offsetLeft + slide.offsetWidth / 2;
      const distance = Math.abs(containerCenter - slideCenter);

      if (distance < closestDistance) {
        closestDistance = distance;
        closestSlideIndex = index;
      }
    });

    if (closestSlideIndex !== this.currentSlide) {
      this.setActiveSlide(closestSlideIndex, false); // Don't scroll, we're already scrolling
    }
  }

  getSlideWidth() {
    if (!this.sliderItems.length) return this.slider.clientWidth;
    
    // Get the width of the first slide including spacing
    const firstSlide = this.sliderItems[0];
    const secondSlide = this.sliderItems[1];
    
    if (secondSlide) {
      // Use the distance between slides as the slide width (includes margins/gaps)
      return secondSlide.offsetLeft - firstSlide.offsetLeft;
    }
    
    // Fallback: use the slide's own width
    return firstSlide.offsetWidth;
  }

  goToSlide(index, smooth = true) {
    const targetIndex = Math.max(0, Math.min(this.sliderItems.length - 1, index));
    
    if (targetIndex === this.currentSlide) {
      // Still snap to ensure proper alignment
      this.snapToSlide(targetIndex);
      return;
    }

    this.setActiveSlide(targetIndex, true, smooth);
  }

  setActiveSlide(index, shouldScroll = true, smooth = true, silent = false) {
    const targetIndex = Math.max(0, Math.min(this.sliderItems.length - 1, index));

    if (targetIndex === this.currentSlide && !shouldScroll) return;

    this.currentSlide = targetIndex;

    // Update slide classes
    this.sliderItems.forEach((slide, i) => {
      slide.classList.toggle('is-active', i === targetIndex);
    });

    // Update dots
    this.updateDots();

    // Update buttons
    this.updateButtonStates();

    // Scroll to slide if needed
    if (shouldScroll) {
      this.snapToSlide(targetIndex, smooth);
    }

    // Dispatch event for media gallery sync (skip on silent init calls)
    if (!silent) {
      this.dispatchEvent(
        new CustomEvent('slideChanged', {
          detail: {
            currentSlide: this.currentSlide,
            currentElement: this.sliderItems[this.currentSlide],
          },
          bubbles: true,
        })
      );
    }
  }

  snapToSlide(index, smooth = true) {
    if (!this.sliderItems[index]) return;

    const targetSlide = this.sliderItems[index];
    const slideLeft = targetSlide.offsetLeft;
    const slideWidth = targetSlide.offsetWidth;
    const containerWidth = this.slider.clientWidth;
    
    let scrollPosition;
    
    if (this.isMobile) {
      // For mobile, snap to start of slide
      scrollPosition = slideLeft;
    } else {
      // For desktop, use native scroll behavior (let CSS handle it)
      // Just scroll to the slide position
      scrollPosition = slideLeft;
    }

    this.slider.scrollTo({
      left: Math.max(0, scrollPosition),
      behavior: smooth ? 'smooth' : 'auto',
    });
  }

  updateDots() {
    if (!this.dotsContainer) return;

    this.dotsContainer.querySelectorAll('li').forEach((dot, index) => {
      dot.classList.toggle('active', index === this.currentSlide);
    });
  }

  updateButtonStates() {
    if (this.prevButton) {
      this.prevButton.disabled = this.currentSlide === 0;
    }
    if (this.nextButton) {
      this.nextButton.disabled = this.currentSlide === this.sliderItems.length - 1;
    }
  }
}

// Register the custom element
if (!customElements.get('media-gallery-slider')) {
  customElements.define('media-gallery-slider', MediaGallerySlider);
}
